import React, { useEffect, useMemo, useState } from "react";
import { BookOpenText, Building2, ChevronDown, ChevronLeft, ChevronRight, Pencil, Plus, Search, Sparkles, Trash2, University, UsersRound } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "universal-cookie";
import { axiosReq } from "../../commons/axiosReq";
import { DeleteModalWriters } from "../../components";
import AddModalCollege from "../../components/MainAdmin/Modals/AddModalCollege";
import CollegeImporter from "../../components/MainAdmin/CollegeImporter";
import "./MainAdminColleges.css";
import "./MainAdminCollegesImporter.css";

const MainAdminColleges = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const selectedUniversityId = Number(state?.id) || null;
  const [items, setItems] = useState([]);
  const [universities, setUniversities] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [row, setRow] = useState(10);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!new Cookies().get("token")) navigate("/", { replace: true });
  }, [navigate]);

  useEffect(() => {
    const loadUniversities = async () => {
      const response = await axiosReq("University/GetUniversitiesForAdmin", "get");
      setUniversities((Array.isArray(response) ? response : []).map((item) => ({ value: item.universityId, label: item.name })));
    };
    loadUniversities();
  }, []);

  useEffect(() => {
    let active = true;
    const loadItems = async () => {
      setLoading(true);
      setError("");
      const query = `page=${page}&pageSize=${row}&search=${encodeURIComponent(search.trim())}`;
      const response = selectedUniversityId
        ? await axiosReq(`College/GetCollegesByUniversity?${query}`, "post", { universityId: selectedUniversityId })
        : await axiosReq(`College/GetColleges?${query}`, "get");
      if (!active) return;
      const result = response?.data || response;
      if (!result || result === "BadRequest") {
        setItems([]);
        setCount(0);
        setError("دریافت دانشکده‌ها با خطا روبه‌رو شد.");
      } else {
        setItems(Array.isArray(result?.data) ? result.data : Array.isArray(result) ? result : []);
        setCount(Number(result?.count ?? (Array.isArray(result) ? result.length : 0)));
      }
      setLoading(false);
    };
    loadItems();
    return () => { active = false; };
  }, [page, reloadKey, row, search, selectedUniversityId]);

  const selectedUniversityName = useMemo(() => {
    const fromItem = items.find((item) => item.universityId === selectedUniversityId)?.university?.name;
    return fromItem || universities.find((item) => item.value === selectedUniversityId)?.label || "";
  }, [items, selectedUniversityId, universities]);

  const refresh = () => setReloadKey((value) => value + 1);
  const start = count === 0 ? 0 : (page - 1) * row + 1;
  const end = Math.min(page * row, count);

  return <div className="colleges-page" dir="rtl">
    <header className="colleges-header">
      <div className="colleges-heading"><span><Building2 size={23} /></span><div><h1>مدیریت دانشکده‌ها</h1><p>{selectedUniversityId ? `دانشکده‌های ${selectedUniversityName || "دانشگاه انتخاب‌شده"}` : "مدیریت دانشکده‌ها و رشته‌های آموزشی همه دانشگاه‌ها"}</p></div></div>
      <div className="colleges-toolbar">
        <label className="colleges-search"><Search size={18} /><input value={search} onChange={(event) => { setSearch(event.target.value); setPage(1); }} placeholder="جستجوی دانشکده یا دانشگاه..." /></label>
        <button className="colleges-add" onClick={() => setShowAddModal(true)}><Plus size={19} />افزودن دانشکده</button>
      </div>
    </header>

    <section className="colleges-summary">
      <div><span><Building2 size={19} /></span><p>تعداد دانشکده‌ها</p><strong>{count.toLocaleString("fa-IR")}</strong></div>
      <div><span><University size={19} /></span><p>دامنه نمایش</p><strong className="summary-text">{selectedUniversityName || "همه دانشگاه‌ها"}</strong></div>
      <div><span><BookOpenText size={19} /></span><p>رشته‌های صفحه جاری</p><strong>{items.reduce((sum, item) => sum + Number(item.fieldCount || 0), 0).toLocaleString("fa-IR")}</strong></div>
    </section>

    <section className={`colleges-import ${showImporter ? "is-open" : ""}`}>
      <button className="colleges-import-toggle" onClick={() => setShowImporter((value) => !value)}>
        <span><Sparkles size={18} /><span><strong>Importer دانشکده‌ها</strong><small>پیدا کردن و ثبت دانشکده‌های پیشنهادی از OpenAlex</small></span></span>
        <b>{showImporter ? "بستن" : "باز کردن"}<ChevronDown size={16} /></b>
      </button>
      {showImporter && <CollegeImporter universities={universities} initialUniversityId={selectedUniversityId} onChanged={refresh} />}
    </section>

    {loading ? <div className="colleges-state">در حال دریافت دانشکده‌ها...</div> : error ? <div className="colleges-state is-error">{error}<button onClick={refresh}>تلاش دوباره</button></div> : items.length === 0 ? <div className="colleges-state"><Building2 size={38} /><strong>دانشکده‌ای پیدا نشد</strong><span>می‌توانید یک دانشکده جدید برای دانشگاه موردنظر اضافه کنید.</span></div> :
      <section className="colleges-grid">{items.map((item) => <article className="college-card" key={item.collegeId}>
        <div className="college-card-head"><span><Building2 size={23} /></span><div><small>دانشکده</small><h2>{item.name || "بدون نام"}</h2></div><b><BookOpenText size={15} />{Number(item.fieldCount || 0).toLocaleString("fa-IR")} رشته</b></div>
        <div className="college-university"><University size={18} /><span><small>دانشگاه مادر</small><strong>{item.university?.name || "ثبت نشده"}</strong></span></div>
        <div className="college-teacher-count"><UsersRound size={17} /><span>تعداد استادها</span><strong>{Number(item.teacherCount || 0).toLocaleString("fa-IR")}</strong></div>
        <div className="college-card-actions">
          <button className="primary" onClick={() => navigate("/mainAdmin/collegeFields", { state: { id: item.collegeId } })}><BookOpenText size={17} />مدیریت رشته‌ها</button>
          <button aria-label="ویرایش" onClick={() => { setSelectedItem(item); setShowEditModal(true); }}><Pencil size={17} /></button>
          <button className="danger" aria-label="حذف" onClick={() => { setSelectedItem(item); setShowDeleteModal(true); }}><Trash2 size={17} /></button>
        </div>
      </article>)}</section>}

    <footer className="colleges-pagination">
      <label>تعداد در صفحه<select value={row} onChange={(event) => { setRow(Number(event.target.value)); setPage(1); }}><option value={5}>۵</option><option value={10}>۱۰</option><option value={25}>۲۵</option></select></label>
      <span>{start.toLocaleString("fa-IR")} تا {end.toLocaleString("fa-IR")} از {count.toLocaleString("fa-IR")}</span>
      <div><button disabled={page === 1} onClick={() => setPage((value) => value - 1)}><ChevronRight size={18} /></button><b>{page.toLocaleString("fa-IR")}</b><button disabled={end >= count} onClick={() => setPage((value) => value + 1)}><ChevronLeft size={18} /></button></div>
    </footer>

    {showAddModal && <AddModalCollege id={selectedUniversityId} universities={universities} title="افزودن دانشکده جدید" buttonName="ثبت" setShowModal={setShowAddModal} reCheck={reloadKey} setRecheck={refresh} setData={setSelectedItem} />}
    {showEditModal && <AddModalCollege id={selectedItem?.universityId || selectedUniversityId} universities={universities} title="ویرایش" buttonName="ویرایش" setShowModal={setShowEditModal} data={selectedItem} reCheck={reloadKey} setRecheck={refresh} />}
    {showDeleteModal && <DeleteModalWriters title="حذف دانشکده" buttonName="حذف" text="آیا از حذف دانشکده انتخاب‌شده اطمینان دارید؟" setShowModal={setShowDeleteModal} api={`College/DeleteCollege/${selectedItem?.collegeId}`} reCheck={reloadKey} setRecheck={refresh} />}
  </div>;
};

export default MainAdminColleges;
